/**
 * The Events tab's whole API surface, in one module.
 *
 * guardrails.md discourages direct post_api calls in components for lacking global
 * loading/error coordination. Overruled deliberately: this tab is page-local, and
 * the coordination lives in useEventsRequest instead of a Redux domain.
 */

import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { get_api, post_api, api_with_method } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';
import * as actions from '../../../redux/action_creators';

// isGo = true is now belt-and-braces — get_api_url already routes /v2 to the Go host.
const events_url = (path) => get_api_url(`/v2/cms_user/marketing${path}`, false, true);

export const error_message = (e) => {
  const data = e?.response?.data;
  return (
    data?.reason ||
    data?.message ||
    data?.error ||
    'Something went wrong. Please contact the dev team.'
  );
};

// A reason reads "event_scheduler: <phrase>: <uuid, uuid>". The run card greps that
// trailing id list to flag rows, so it has to survive; it just should not be shown.
const MACHINE_ID =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\\d{4}-\\d{2}-\\d{2}T[\\d:]+Z';
const MACHINE_TAIL = new RegExp(`:\\s*(?:${MACHINE_ID})(?:\\s*,\\s*(?:${MACHINE_ID}))*\\s*$`, 'i');

export const clean_message = (raw) => {
  const stripped = (raw || '')
    .replace(/^event_scheduler:\s*/i, '')
    .replace(MACHINE_TAIL, '')
    .trim();
  return stripped ? stripped.charAt(0).toUpperCase() + stripped.slice(1) : raw;
};

// ─── Endpoints ───────────────────────────────────────────────────────────────

export const list_events = () => get_api(events_url('/events'), true);

export const create_event = (payload) => post_api(events_url('/events'), payload, true);

export const get_event = (slug) => get_api(events_url(`/events/${encodeURIComponent(slug)}`), true);

export const update_event = (topic_uuid, payload) =>
  api_with_method('patch', events_url(`/events/${topic_uuid}`), payload, true);

export const create_run = (topic_uuid, payload) =>
  post_api(events_url(`/events/${topic_uuid}/runs`), payload, true);

export const update_run = (run_uuid, payload) =>
  api_with_method('patch', events_url(`/runs/${run_uuid}`), payload, true);

export const publish_run = (run_uuid) =>
  post_api(events_url(`/runs/${run_uuid}/publish`), null, true);

export const delete_run = (run_uuid) =>
  api_with_method('delete', events_url(`/runs/${run_uuid}`), null, true);

export const cancel_run = (run_uuid) =>
  post_api(events_url(`/runs/${run_uuid}/cancel`), null, true);

export const duplicate_run = (run_uuid, run_dates) =>
  post_api(events_url(`/runs/${run_uuid}/duplicate`), { run_dates }, true);

export const cancel_slot = (slot_uuid) =>
  post_api(events_url(`/slots/${slot_uuid}/cancel`), null, true);

export const list_registrations = (run_uuid) =>
  get_api(events_url(`/runs/${run_uuid}/registrations`), true);

/** Keyset-paginated on `before`; an offset would skip or repeat rows mid-scroll. */
export const list_activity = (topic_uuid, { limit, before } = {}) => {
  const query = new URLSearchParams();
  if (limit) query.set('limit', String(limit));
  if (before) query.set('before', before);

  const suffix = query.toString() ? `?${query.toString()}` : '';
  return get_api(events_url(`/events/${topic_uuid}/activity${suffix}`), true);
};

/**
 * Owns loading and error handling for every call in this tab. `run` never throws —
 * it resolves to { data, error }. Pass { silent: true } when the caller renders the
 * message itself rather than as a toast.
 */
export const useEventsRequest = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const run = useCallback(
    async (request, { silent = false } = {}) => {
      setLoading(true);
      try {
        // api_funcs resolve the whole axios response, so unwrap one level here
        // rather than at twelve call sites.
        return { data: (await request())?.data ?? null, error: null };
      } catch (e) {
        const message = error_message(e);
        if (!silent) {
          // Cleaned for the toast only. The raw message is still returned, because
          // callers grep it for the slot ids the server named.
          dispatch(actions.set_notification_variable(true, 'error', clean_message(message)));
        }
        return { data: null, error: message };
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  return { run, loading };
};
